/**
 * Where it is safe to send someone after they sign in.
 *
 * The return path arrives in the query string, so it is attacker-controlled:
 * anyone can mail out `/login?returnTo=https://not-us.example/login` and the
 * link is genuinely from aitools.yoursmartstack.com, genuinely shows Caren's
 * real sign-in form, and genuinely signs the person in before dropping them on
 * a page someone else controls. That is a textbook open redirect, and a login
 * page is the worst place to have one: the visitor has just been taught that
 * this flow is trustworthy.
 *
 * SECURITY FIX, Aug 2026: AuthModal already did `window.location.href =
 * options.returnTo` with no check at all, and /auth has been reading returnTo
 * off the query string and handing it straight over since the modal replaced
 * the auth page. Every entry point now goes through this function.
 *
 * We never try to repair a bad value, because "make the attacker's URL safe"
 * is a game with no end. A rejected value falls back to the site root.
 *
 * ── Why this is not same-origin-only ──────────────────────────────────────
 *
 * BUG FIX, Aug 2026: the first version of this guard reduced *every* candidate
 * to a same-origin path, which was too narrow and broke a flow that matters.
 * This app is the identity source of truth for the whole Smart Stack family,
 * so signing in here is how you sign in to Quick Wins
 * (claude.yoursmartstack.com) as well. Someone sent here from a sibling app
 * arrives with `?returnTo=https://claude.yoursmartstack.com/...`, and the
 * guard collapsed that to "/": they authenticated successfully and landed on
 * this site's home page, with no error and nothing in any log. From their side
 * it read as "logging in did not work".
 *
 * So a sibling origin is allowed through as an absolute URL. The rule is
 * deliberately a suffix match on the registrable domain rather than a list of
 * app origins, because `products.origin` is the source of truth for what apps
 * exist and adding an app is supposed to be a row in that table, not an edit
 * to this file. A per-app list here would mean the third Smart Stack app
 * silently gets the same broken login flow Quick Wins just had.
 *
 * The trust boundary that buys: every host under yoursmartstack.com is one we
 * operate. If a subdomain ever serves user-generated content or grows its own
 * open redirect, this becomes a way to chain through it — so that is the thing
 * to re-examine before pointing a subdomain at anything we do not control.
 */

/** Where people land when there is no usable return path. */
export const DEFAULT_REDIRECT = "/";

/** The origin used to parse a candidate path. Never navigated to. */
const PROBE_ORIGIN = "https://redirect-check.invalid";

/**
 * The registrable domain every Smart Stack app is served from.
 *
 * Matched as the exact host or a subdomain of it. Never as a substring:
 * "evil-yoursmartstack.com" and "yoursmartstack.com.evil.example" are both
 * different registrable domains that a careless `includes()` would accept.
 */
const FAMILY_DOMAIN = "yoursmartstack.com";

/**
 * Is this a sibling app we are willing to hand an authenticated person to?
 *
 * https only. A cross-origin redirect to http is a downgrade we have no reason
 * to allow, and every app in the family is served over TLS.
 */
function isFamilyOrigin(url: URL): boolean {
  if (url.protocol !== "https:") return false;
  // URL lowercases the hostname, and puts anything before an "@" in
  // `username`/`password` rather than `hostname` — so the userinfo trick
  // ("https://claude.yoursmartstack.com@evil.example/") parses with
  // hostname "evil.example" and is refused here.
  return (
    url.hostname === FAMILY_DOMAIN || url.hostname.endsWith(`.${FAMILY_DOMAIN}`)
  );
}

/**
 * Reduce a caller-supplied return path to one we are willing to navigate to.
 *
 * Accepts a path beginning with a single "/", or an absolute https URL on a
 * Smart Stack host. Returns DEFAULT_REDIRECT for everything else.
 */
export function safeRedirect(raw: string | null | undefined): string {
  if (!raw) return DEFAULT_REDIRECT;

  // The value is usually encoded once by whoever built the link. Decoding can
  // throw on a malformed escape ("%zz"), which is itself a good reason to
  // refuse the value rather than pass the raw form through.
  let value: string;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return DEFAULT_REDIRECT;
  }

  value = value.trim();
  if (!value) return DEFAULT_REDIRECT;

  // Control characters, including the tab and newline a browser strips out of
  // a URL before parsing it. "/\tjavascript:alert(1)" is a real bypass against
  // naive checks, because the check sees the tab and the browser does not.
  // This runs before anything else, so it covers the absolute-URL branch too.
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(value)) return DEFAULT_REDIRECT;

  // An absolute URL is only allowed when it names a sibling Smart Stack app.
  // Parsed with no base, so this branch is entered only by a value that is
  // already absolute — a bare path throws here and falls through to the path
  // handling below, which is where it belongs.
  //
  // Note this is checked before the "must start with /" rule, and that the
  // rule still rejects "//evil.example": a protocol-relative URL has no scheme
  // of its own, so it does not parse as absolute and never reaches here.
  try {
    const absolute = new URL(value);
    if (isFamilyOrigin(absolute)) return absolute.toString();
    return DEFAULT_REDIRECT;
  } catch {
    // Not an absolute URL. Fall through to the same-origin path rules.
  }

  // Must be path-absolute.
  if (!value.startsWith("/")) return DEFAULT_REDIRECT;

  // "//evil.example" and "/\evil.example" are protocol-relative URLs: the
  // browser reads them as a different host, not as a path on ours. Backslash
  // counts because browsers normalise it to a forward slash.
  if (value.startsWith("//") || value.startsWith("/\\"))
    return DEFAULT_REDIRECT;

  // Belt and braces: resolve against a throwaway origin and confirm nothing in
  // the string moved us off it, using the same parser the browser will.
  try {
    const probe = new URL(value, PROBE_ORIGIN);
    if (probe.origin !== PROBE_ORIGIN) return DEFAULT_REDIRECT;
    return `${probe.pathname}${probe.search}${probe.hash}`;
  } catch {
    return DEFAULT_REDIRECT;
  }
}

/**
 * Read the return path out of a query string.
 *
 * `returnTo` is the name the rest of the app uses: ProtectedRoute writes it,
 * /auth reads it, and it is in GoHighLevel links that have already been sent.
 * `redirect` and `next` are accepted as aliases because they are what anyone
 * hand-building a link will reach for first, and a login link that silently
 * drops its destination is a bug report nobody can reproduce.
 */
export function readRedirectParam(search: string): string {
  const params = new URLSearchParams(search);
  const raw =
    params.get("returnTo") ?? params.get("redirect") ?? params.get("next");
  return safeRedirect(raw);
}
